import {
  faCheckDouble,
  faExclamationTriangle,
  faSpinner
} from '@fortawesome/pro-solid-svg-icons'
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router'
import clsx from 'clsx'
import { AnimatePresence } from 'motion/react'
import type { ReactElement } from 'react'
import { useCallback, useContext, useMemo, useState } from 'react'
import { z } from 'zod'
import {
  type DataTableRowSelection,
  type DataTableSort,
  useDataTableSearchParams
} from '~/components/DataTable/DataTable'
import { FilterBar } from '~/components/Filters/FilterBar/FilterBar'
import type { FiltersState } from '~/components/Filters/FilterBar/types'
import ActionBar from '~/components/Global/Blocks/ActionBar/ActionBar'
import { Paper } from '~/components/Global/Blocks/Paper/Paper'
import { Loader } from '~/components/Global/Elements/Loader/Loader'
import { NoResult } from '~/components/Global/Elements/NoResult/NoResult'
import { Tooltip } from '~/components/Global/Elements/Tooltip/Tooltip'
import { Button } from '~/components/Global/Interactions/Button/Button'
import { ButtonIcon } from '~/components/Global/Interactions/Button/ButtonIcon'
import { ButtonVariation } from '~/components/Global/Interactions/Button/ButtonVariation'
import { buildCompanyContactIdsSearch } from '~/domains/companies/companySearch'
import { CompaniesTable } from '~/domains/companies/components/CompaniesTable'
import { useCompaniesFilterDefinitions } from '~/domains/companies/hooks/useCompaniesFilterDefinitions'
import { useCompanyContactSelection } from '~/domains/companies/hooks/useCompanyContactSelection'
import { useCompanyList } from '~/domains/companies/queries/companyList'
import { useConfig } from '~/domains/configuration/queries/config'
import { ContactsBulkActions } from '~/domains/contacts/components/ContactsBulkActions/ContactsBulkActions'
import {
  contentWrapper,
  contentWrapperWide
} from '~/pages/styles/Dashboard.css'
import { listWrapper, quickControlsBar } from '~/pages/styles/Orders.css'
import { AuthContext } from '~/utils/contextes'
import useDocumentTitle from '~/utils/documentTitle'
import { urlNumber } from '~/utils/zod'
import { Header } from './-components/Header'

const searchSchema = z
  .object({
    page: urlNumber,
    sort: z.string().optional()
  })
  .catchall(z.string())

const defaultSorting: DataTableSort[] = [{ id: 'name', desc: false }]

const Companies = (): ReactElement => {
  useDocumentTitle('Entreprises')
  const currentUser = useContext(AuthContext)
  const { data: currentUserDefaultFilter } = useConfig(
    'listing.contacts_current_user_filter'
  )
  const filterDefinitions = useCompaniesFilterDefinitions()

  const search = useSearch({ from: '/_dashboard/companies/' })
  const navigate = useNavigate({ from: '/companies/' })

  const navigateSearch = useCallback(
    (opts: { search: Record<string, string>; replace?: boolean }) => {
      void navigate({
        search: opts.search,
        replace: opts.replace
      })
    },
    [navigate]
  )

  const {
    queryParams,
    setQueryParams,
    panelFilters,
    setPanelFilters,
    resetAllFilters
  } = useDataTableSearchParams({
    search,
    navigate: navigateSearch,
    defaultSorting
  })

  const [defaultsResolved, setDefaultsResolved] = useState(false)
  const { data, totalItems, isLoading, isFetching, isError, refetch } =
    useCompanyList(
      queryParams,
      panelFilters,
      filterDefinitions,
      defaultsResolved
    )

  const {
    selectedContactIds,
    selectedCountByCompany,
    isAllSelected,
    isSelectingAll,
    toggleContact,
    removeContact,
    selectCompanies,
    unselectCompanies,
    selectAll,
    clear
  } = useCompanyContactSelection(panelFilters, queryParams.columnFilters)

  const rowSelection = useMemo<DataTableRowSelection>(
    () =>
      Object.fromEntries(
        data
          .filter(
            (company) =>
              company.matchingContactsCount > 0 &&
              (selectedCountByCompany.get(company.id) ?? 0) >=
                company.matchingContactsCount
          )
          .map((company) => [String(company.id), true])
      ),
    [data, selectedCountByCompany]
  )

  const handleRowSelectionChange = useCallback(
    (next: DataTableRowSelection) => {
      const isSelected = (selection: DataTableRowSelection, id: number) =>
        selection[String(id)] === true
      const companies = data.filter(
        (company) => company.matchingContactsCount > 0
      )
      const toUnselect = companies
        .filter(
          ({ id }) => isSelected(rowSelection, id) && !isSelected(next, id)
        )
        .map(({ id }) => id)
      const toSelect = companies.filter(
        ({ id }) => !isSelected(rowSelection, id) && isSelected(next, id)
      )
      if (toUnselect.length > 0) {
        unselectCompanies(toUnselect)
      }
      if (toSelect.length > 0) {
        selectCompanies(toSelect)
      }
    },
    [data, rowSelection, selectCompanies, unselectCompanies]
  )

  const toggleSelectAll = () => {
    if (isAllSelected) {
      clear()
      return
    }
    void selectAll(
      buildCompanyContactIdsSearch(queryParams, panelFilters, filterDefinitions)
    )
  }

  const hasActiveFilters =
    queryParams.columnFilters.length > 0 || Object.keys(panelFilters).length > 0

  if (currentUserDefaultFilter === undefined) {
    return <Loader />
  }

  const defaultFilters: FiltersState = { archived: 'false' }
  if (currentUserDefaultFilter && currentUser) {
    defaultFilters.linkedUsers = String(currentUser.id)
  }

  return (
    <div className={clsx(contentWrapper, contentWrapperWide)}>
      <Header />
      <Paper className={listWrapper}>
        <div className={quickControlsBar}>
          <FilterBar
            definitions={filterDefinitions}
            filters={panelFilters}
            onChange={setPanelFilters}
            savedSearchesKey="search.saved_companies"
            defaultFilters={defaultFilters}
            onDefaultsResolved={() => setDefaultsResolved(true)}
            analyticsKey="companies"
          />
        </div>
        {isError ? (
          <NoResult
            isError
            icon={faExclamationTriangle}
            message="Impossible de charger les entreprises"
            onDismiss={() => void refetch()}
            dismissLabel="Réessayer"
          />
        ) : (
          <CompaniesTable
            data={data}
            totalItems={totalItems}
            queryParams={queryParams}
            filters={panelFilters}
            rowSelection={rowSelection}
            onRowSelectionChange={handleRowSelectionChange}
            selectedContactIds={selectedContactIds}
            onToggleContact={toggleContact}
            onQueryChange={setQueryParams}
            onResetFilters={resetAllFilters}
            showResetFilters={hasActiveFilters}
            isLoading={!defaultsResolved || isLoading}
            isFetching={isFetching}
            onRefresh={() => void refetch()}
          />
        )}
      </Paper>
      <AnimatePresence>
        {selectedContactIds.length > 0 && (
          <ActionBar
            text={`${selectedContactIds.length} contact(s) sélectionné(s)`}
            onDismiss={clear}
          >
            <Tooltip
              text={isAllSelected ? 'Désélectionner tout' : 'Sélectionner tout'}
              direction="top"
            >
              <Button
                variation={ButtonVariation.PrimaryInversed}
                onClick={toggleSelectAll}
                disabled={isSelectingAll}
                narrow
              >
                <ButtonIcon
                  icon={isSelectingAll ? faSpinner : faCheckDouble}
                  spin={isSelectingAll}
                />
              </Button>
            </Tooltip>
            <ContactsBulkActions
              contactIds={selectedContactIds}
              onRemoveContact={removeContact}
            />
          </ActionBar>
        )}
      </AnimatePresence>
    </div>
  )
}

export const Route = createFileRoute('/_dashboard/companies/')({
  validateSearch: searchSchema,
  component: Companies
})
